import { Box, BoxProps, Grid2, Stack } from '@mui/material';
import { ComponentType, ReactNode } from 'react';

import { useReadyToScale } from '@/components/diagrams/FlowChart/hooks/useReadyToScale';
import { Card } from '@/components/layout/Card/Card';
import { Markdown } from '@/components/Markdown/Markdown';
import { TextEditorProvider } from '@/components/TextEditor/contexts/TextEditor.provider';
import { getEntityNotesForDeath } from '@/modules/entities/utils/getEntityNotesForDeath';
import { getEntityDiagramVariant } from '@/modules/entityDiagram/hooks/useEntityDiagramVariant';
import { CustomSlideTextEditorInner } from '@/modules/presentation/clientPresentation/components/slides/CustomSlide/CustomSlide.components';
import { EntityDiagram } from '@/modules/presentation/entities/components/EntityDiagram';
import { PresentationSlide } from '@/modules/presentation/PresentationSlide';
import { PresentationSlideProps } from '@/modules/presentation/PresentationSlide/PresentationSlide';

import { EntityDiagramAndNotesSlide_EntityFragment } from './graphql/EntityDiagramAndNotesSlide.generated';

export interface EntityDiagramAndNotesSlideProps {
  entity: EntityDiagramAndNotesSlide_EntityFragment;
  slideId: string;
  deathOrderId?: string | null | undefined;
  footer?: ReactNode;
  Slide?: ComponentType<PresentationSlideProps>;
  SlideWrapper?: ComponentType<BoxProps>;
  /** If true, only display the diagram, even if the notes are present. */
  hideNotes?: boolean;
  /**
   * Enables the diagram's design-mode hover/edit chrome. Only the interactive
   * `PreviewAwareEntityDiagramAndNotesSlide` turns this on; the read-only
   * export/print path leaves it off so the chrome (and its react-hook-form-aware
   * providers) never mounts.
   */
  interactiveEditing?: boolean;
  /**
   * Renders the notes column, given the resolved notes for the active death.
   * Defaults to a read-only markdown view; the interactive slide passes the
   * editable notes UI. Its presence forces the notes column to show, so an empty
   * editable slide still offers the "add notes" affordance.
   */
  renderNotes?: (displayNotes: string) => ReactNode;
}

/**
 * Read-only entity diagram + notes slide: a two-column layout of the diagram and
 * its notes, plus the measure-before-scale wiring. Used directly by the
 * export/print path. Interactivity (the diagram's edit chrome via
 * `interactiveEditing`, and editable notes via `renderNotes`) is injected by
 * `PreviewAwareEntityDiagramAndNotesSlide`; with neither, this module imports no
 * react-hook-form-aware editing code.
 */
export function EntityDiagramAndNotesSlide({
  entity,
  slideId,
  deathOrderId,
  footer,
  Slide = PresentationSlide.Slide,
  SlideWrapper = Box,
  hideNotes = false,
  interactiveEditing = false,
  renderNotes,
}: EntityDiagramAndNotesSlideProps) {
  // Measure-before-scale: scale only once the diagram reports ready, and on
  // edits remount it (`remeasure`, driven by the diagram's data-change callback)
  // so React Flow re-initializes from scratch and reports ready again. An
  // in-place data change (an entity edit, a sub-trust edit, or a saved diagram
  // layout) doesn't move React Flow's own ready signal and can leave node
  // internals stale, which misaligns edges when we re-apply the scale — the
  // remount is what reliably clears that. Do not force readiness on a timer:
  // a large print can take longer than the fallback to measure its diagrams.
  const { readyToScale, registerReady, remountKey, remeasure } =
    useReadyToScale({ rescaleFallbackMs: null });

  const displayNotes = getEntityNotesForDeath(entity, deathOrderId) || '';

  let shouldDisplayNotes = !!renderNotes || !!displayNotes;
  if (hideNotes) {
    shouldDisplayNotes = false;
  }

  return (
    <SlideWrapper>
      <Slide
        id={slideId}
        footer={footer || <PresentationSlide.Footer />}
        leftHeaderContent={
          <PresentationSlide.MainHeading
            heading={entity.subtype.displayName}
            subheading={entity.extendedDisplayKind}
          />
        }
        readyToScale={readyToScale}
        data-presentationentityready={readyToScale.toString()}
      >
        <Stack width="100%" height="100%" sx={{ px: 2 }} spacing={2}>
          <Grid2
            container
            columns={2}
            direction="row"
            columnSpacing={2}
            flexGrow={1}
          >
            <Grid2 size={shouldDisplayNotes ? 1 : 2}>
              <EntityDiagram
                key={`entity-diagram-${deathOrderId}`}
                entityId={entity.id}
                householdId={entity.household.id}
                interactiveEditing={interactiveEditing}
                registerDiagramReady={registerReady}
                redrawKey={remountKey}
                onDiagramDataChange={remeasure}
                readyToScale={readyToScale}
                entityDiagramVariant={getEntityDiagramVariant(entity.kind)}
                firstDecedentId={deathOrderId}
                isInHalfWidthContainer={shouldDisplayNotes}
                fitToCanvas={true}
                registrationProps={{
                  slideId,
                  bundleId: '',
                  bundleIndex: 0,
                }}
              />
            </Grid2>
            {shouldDisplayNotes && (
              <Grid2 size={1}>
                <TextEditorProvider theme="presentationContent">
                  <Box
                    position="relative"
                    height="100%"
                    width="100%"
                    data-testid="relative-container"
                  >
                    <Box
                      position="absolute"
                      top={0}
                      left={0}
                      right={0}
                      bottom={0}
                      data-testid="absolute-container"
                    >
                      {renderNotes ? (
                        renderNotes(displayNotes)
                      ) : (
                        <StaticNotesDisplay displayNotes={displayNotes} />
                      )}
                    </Box>
                  </Box>
                </TextEditorProvider>
              </Grid2>
            )}
          </Grid2>
        </Stack>
      </Slide>
    </SlideWrapper>
  );
}

function StaticNotesDisplay({ displayNotes }: { displayNotes: string }) {
  return (
    <Card variant="outlined" sx={{ height: '100%', width: '100%', p: 2 }}>
      <Markdown
        markdown={displayNotes}
        EditorInner={CustomSlideTextEditorInner}
      />
    </Card>
  );
}
